import { Upload } from "lucide-react";
import { type DragEvent, useState } from "react";

export type AttachmentKind = "script" | "reference" | "asset";

export function validateAttachmentPath(path: string, kind: AttachmentKind): string | null {
  if (!path || path.trim() === "") {
    return "Path cannot be empty";
  }

  if (path.startsWith("/")) {
    return "Path must be relative (no leading '/')";
  }

  if (path.split("/").some((segment) => segment === "..")) {
    return "Path must not contain '..'";
  }

  // Accept both singular and plural prefix
  const expectedPluralPrefix = `${kind}s/`;
  const expectedSingularPrefix = `${kind}/`;

  if (!path.startsWith(expectedPluralPrefix) && !path.startsWith(expectedSingularPrefix)) {
    return `Path must start with '${expectedPluralPrefix}' or '${expectedSingularPrefix}'`;
  }

  return null;
}

interface AttachmentDropzoneProps {
  kind: AttachmentKind;
  onKindChange: (kind: AttachmentKind) => void;
  onFileDrop: (file: File, path: string, kind: AttachmentKind) => void;
}

export function AttachmentDropzone({ kind, onKindChange, onFileDrop }: AttachmentDropzoneProps) {
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    setError(null);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const file = files[0];
    if (!file) return;

    const constructedPath = `${kind}s/${file.name}`;

    const validationError = validateAttachmentPath(constructedPath, kind);
    if (validationError) {
      setError(validationError);
      return;
    }

    onFileDrop(file, constructedPath, kind);
  };

  const kinds: AttachmentKind[] = ["script", "reference", "asset"];

  return (
    <div>
      <div className="mb-3 flex gap-2">
        {kinds.map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => {
              onKindChange(k);
              setError(null);
            }}
            className={`rounded px-3 py-1.5 text-sm capitalize transition-colors ${
              kind === k ? "bg-teal-500 text-white" : "bg-surface text-muted hover:text-ink"
            }`}
          >
            {k}
          </button>
        ))}
      </div>

      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`flex min-h-32 flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors ${
          dragOver ? "border-teal-500 bg-teal-500/5" : "border-border bg-surface/50"
        }`}
      >
        <Upload size={32} className={dragOver ? "text-teal-500" : "text-muted"} />
        <p className="mt-2 text-sm text-muted">Drop {kind} file here</p>
        <p className="mt-1 text-xs text-muted/70">Will be saved to {kind}s/</p>
      </div>

      {error && <p className="mt-2 text-sm text-rose-600 dark:text-rose-400">{error}</p>}
    </div>
  );
}
